import { ElementRef } from '@angular/core';
import { startMomentumScrolling as runMomentumScroll, stopMomentumScrolling as stopMomentumScroll, applyHorizontalDragMove } from '../../shared/utils/momentum-scroll.util';

export interface DragScrollConfig {
  scrollWrapper: ElementRef;
  isDragging: boolean;
  startX: number;
  startY: number;
  scrollLeft: number;
  lastX: number;
  lastY: number;
  velocityX: number;
  lastMoveTime: number;
  momentumAnimation: number | null;
  scrollDirection: 'horizontal' | 'vertical' | null;
  hasDeterminedDirection: boolean;
}

export class TableUtils {
  /**
   * Create a default DragScrollConfig for a given scroll wrapper element
   */
  static createDragScrollConfig(scrollWrapper: ElementRef): DragScrollConfig {
    return {
      scrollWrapper,
      isDragging: false,
      startX: 0,
      startY: 0,
      scrollLeft: 0,
      lastX: 0,
      lastY: 0,
      velocityX: 0,
      lastMoveTime: 0,
      momentumAnimation: null,
      scrollDirection: null,
      hasDeterminedDirection: false,
    };
  }

  /**
   * Check if an element is interactive (should not trigger drag)
   */
  static isInteractiveElement(target: HTMLElement): boolean {
    const interactiveElements = ['BUTTON', 'A', 'INPUT', 'SELECT'];
    const interactiveClasses = ['.action-button', '.order-link', '.employee-link', '.clickable-name'];
    
    return interactiveElements.includes(target.tagName) || 
           interactiveClasses.some(cls => target.closest(cls));
  }

  /**
   * Initialize drag scrolling for a table
   */
  static initializeDragScrolling(config: DragScrollConfig): void {
    const scrollWrapper = config?.scrollWrapper?.nativeElement;
    if (!scrollWrapper) return;
    
    // Mouse events
    scrollWrapper.addEventListener('mousedown', (e: MouseEvent) => {
      if (TableUtils.isInteractiveElement(e.target as HTMLElement)) return;
      TableUtils.handleDragStart(e.pageX, config);
    });

    scrollWrapper.addEventListener('mousemove', (e: MouseEvent) => {
      if (!config.isDragging) return;
      e.preventDefault();
      TableUtils.handleDragMove(e.pageX, config);
    });

    scrollWrapper.addEventListener('mouseup', () => {
      TableUtils.handleDragEnd(config);
    });

    scrollWrapper.addEventListener('mouseleave', () => {
      TableUtils.handleDragEnd(config);
    });

    // Touch events
    scrollWrapper.addEventListener('touchstart', (e: TouchEvent) => {
      if (TableUtils.isInteractiveElement(e.target as HTMLElement)) return;
      TableUtils.handleTouchStart(e.touches[0].pageX, e.touches[0].pageY, config);
    }, { passive: true });

    scrollWrapper.addEventListener('touchmove', (e: TouchEvent) => {
      if (!config.isDragging) return;
      
      const touch = e.touches[0];
      const deltaX = Math.abs(touch.pageX - config.startX);
      const deltaY = Math.abs(touch.pageY - config.startY);
      
      // Determine scroll direction on first significant movement
      if (!config.hasDeterminedDirection && (deltaX > 5 || deltaY > 5)) {
        config.hasDeterminedDirection = true;
        config.scrollDirection = deltaX > deltaY ? 'horizontal' : 'vertical';
        
        // If vertical scrolling detected, stop dragging to allow natural scroll
        if (config.scrollDirection === 'vertical') {
          config.isDragging = false;
          config.scrollWrapper.nativeElement.style.cursor = 'grab';
          config.scrollWrapper.nativeElement.style.userSelect = 'auto';
          document.body.style.userSelect = 'auto';
          (document.body.style as any).webkitUserSelect = 'auto';
          (document.body.style as any).mozUserSelect = 'auto';
          (document.body.style as any).msUserSelect = 'auto';
          return; // Allow natural vertical scrolling
        }
      }
      
      // Only prevent default and handle horizontal scrolling if direction is horizontal
      if (config.scrollDirection === 'horizontal') {
        e.preventDefault();
        TableUtils.handleDragMove(touch.pageX, config);
      }
      // If vertical, allow natural scrolling by not preventing default
    }, { passive: false });

    scrollWrapper.addEventListener('touchend', () => {
      TableUtils.handleDragEnd(config);
    });

    // Prevent default drag behavior on images and links within the table
    scrollWrapper.addEventListener('dragstart', (e: Event) => e.preventDefault());
  }

  private static handleTouchStart(pageX: number, pageY: number, config: DragScrollConfig): void {
    TableUtils.stopMomentumAnimation(config);
    config.startY = pageY;
    config.lastY = pageY;
    config.scrollDirection = null;
    config.hasDeterminedDirection = false;
    TableUtils.startDrag(pageX - config.scrollWrapper.nativeElement.offsetLeft, config);
  }

  private static handleDragStart(pageX: number, config: DragScrollConfig): void {
    TableUtils.stopMomentumAnimation(config);
    config.scrollDirection = 'horizontal'; // Mouse is always horizontal
    config.hasDeterminedDirection = true;
    TableUtils.startDrag(pageX - config.scrollWrapper.nativeElement.offsetLeft, config);
  }

  private static handleDragMove(pageX: number, config: DragScrollConfig): void {
    if (!config.isDragging) return;
    applyHorizontalDragMove(pageX, config);
  }

  /**
  * Restore UI state after a drag-to-scroll interaction ends; resets dragging flags, restores cursor and text selection, and starts momentum scrolling for horizontal drags.
  * @example
  * handleDragEnd(config)
  * undefined
  * @param {DragScrollConfig} config - Configuration object containing drag state, scroll wrapper element, and scrolling settings.
  * @returns {void} No value is returned.
  **/
  private static handleDragEnd(config: DragScrollConfig): void {
    if (config.isDragging) {
      config.isDragging = false;
      
      // Only restore cursor and user-select if we were doing horizontal scrolling
      if (config.scrollDirection === 'horizontal' || config.scrollDirection === null) {
        config.scrollWrapper.nativeElement.style.cursor = 'grab';
        config.scrollWrapper.nativeElement.style.userSelect = 'auto';
        
        // Restore text selection on the entire document
        document.body.style.userSelect = 'auto';
        (document.body.style as any).webkitUserSelect = 'auto';
        (document.body.style as any).mozUserSelect = 'auto';
        (document.body.style as any).msUserSelect = 'auto';
        
        // Start momentum scrolling if there's sufficient velocity (only for horizontal)
        if (config.scrollDirection === 'horizontal') {
          TableUtils.startMomentumScrolling(config);
        }
      }
      
      // Reset direction tracking
      config.scrollDirection = null;
      config.hasDeterminedDirection = false;
    }
  }

  /**
  * Initialize drag state and apply temporary styles to enable horizontal drag-to-scroll.
  * @example
  * startDrag(120, myDragConfig)
  * undefined
  * @param {{number}} {{startX}} - The X coordinate (clientX) where the drag started.
  * @param {{DragScrollConfig}} {{config}} - The drag-scroll configuration and state (contains scrollWrapper, scrollDirection, and flags).
  * @returns {{void}} No return value.
  **/
  private static startDrag(startX: number, config: DragScrollConfig): void {
    config.isDragging = true;
    config.startX = startX;
    config.scrollLeft = config.scrollWrapper.nativeElement.scrollLeft;
    
    // Only apply cursor and user-select for horizontal scrolling
    if (config.scrollDirection === 'horizontal' || config.scrollDirection === null) {
      config.scrollWrapper.nativeElement.style.cursor = 'grabbing';
      config.scrollWrapper.nativeElement.style.userSelect = 'none';
      
      // Prevent text selection on the entire document during drag (only for horizontal)
      document.body.style.userSelect = 'none';
      (document.body.style as any).webkitUserSelect = 'none';
      (document.body.style as any).mozUserSelect = 'none';
      (document.body.style as any).msUserSelect = 'none';
    }
  }

  private static startMomentumScrolling(config: DragScrollConfig): void {
    runMomentumScroll(config);
  }

  private static stopMomentumAnimation(config: DragScrollConfig): void {
    stopMomentumScroll(config);
  }

  /**
   * Cleanup method to restore text selection if component is destroyed while dragging
   */
  static cleanup(): void {
    document.body.style.userSelect = 'auto';
    (document.body.style as any).webkitUserSelect = 'auto';
    (document.body.style as any).mozUserSelect = 'auto';
    (document.body.style as any).msUserSelect = 'auto';
  }
}
